<script setup lang="ts">
import { GUEST_MODE } from '@/guest/guestMode'
import { useAuthStore } from '@/stores/auth'
import { supabase } from '@/supabase'
// import { registerLicense } from '@syncfusion/ej2-base'
// import LogRocket from 'logrocket'
// import { Analytics } from '@vercel/analytics/vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import { defineAsyncComponent, ref, watch } from 'vue'
import useTheme from '@/composables/useTheme'
import { THEME_PAGE_DARK, THEME_PAGE_LIGHT } from '@/theme/appearance'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import { syncMetaPixel } from '@/utils/loadMetaPixel'
import { useInvoiceEditorOverlay } from '@/composables/useInvoiceEditorOverlay'
import { filePreviewRequested } from '@/components/file-preview/previewRequested'
import ToastContainer from '@/components/ToastContainer.vue'
import InAppBanner from '@/native/InAppBanner.vue'
import OfflineBanner from '@/native/OfflineBanner.vue'
import { clearBootCache } from '@/native/bootCache'
import ActionSheetHost from '@/components/mobile/ActionSheetHost.vue'
import ImpersonationBanner from '@/components/ImpersonationBanner.vue'
import SupportAccessBanner from '@/components/support/SupportAccessBanner.vue'
import AppGates from '@/components/gates/AppGates.vue'
import VoiceCallHost from '@/components/voice/VoiceCallHost.vue'
import useToast from '@/composables/useToast'
import useImpersonation from '@/composables/useImpersonation'
import { startSystemAnnouncementListener, stopSystemAnnouncementListener } from '@/composables/useSystemAnnouncement'
import { useActivityHeartbeat } from '@/composables/useActivityHeartbeat'
import { isProductionEnv, signedInHomeRoute } from '@/utils/env'
import { getNativePlatform, isDesktopShell, isNativePlatform } from '@/utils/platform'
import { nativeGoogleAuthPending } from '@/utils/nativeAuthOAuth'
import { installNativeZoomLock } from '@/utils/nativeZoomLock'
import { applyNativeKeyboardMode } from '@/utils/nativeAuthKeyboard'
import { captureReferralFromQuery, claimStoredReferral } from '@/utils/referral'
import { maybeStitchIdentity } from '@/analytics/track'
import { isDesktopApp } from '@/desktop/bridge'

const KeyboardShortcutsModal = defineAsyncComponent(() => import('@/desktop/KeyboardShortcutsModal.vue'))

const { showToast } = useToast()
const { reconcileImpersonation } = useImpersonation()
const { toggleLightDark, setThemePreference } = useTheme()
/** Inside the Lighthouse desktop app (Electron, with its bridge). */
const desktopApp = isDesktopApp()

/**
 * App-level overlays stay out of the launch: the invoice editor pulls in pdf.js and the cover-email composer, and
 * the file previewer its renderers. Each mounts (closed) once its chunk has loaded while the app is idle, or at
 * once on a first open that beats the idle load; from then on they stay mounted, so opens animate as before.
 */
const InvoiceOverlayMount = defineAsyncComponent(() => import('@/components/invoices/InvoiceOverlayMount.vue'))
const FilePreviewHost = defineAsyncComponent(() => import('@/components/file-preview/FilePreviewHost.vue'))
const { overlayInvoiceId, composerInvoiceId } = useInvoiceEditorOverlay()
const invoiceOverlayUsed = ref(false)
const filePreviewUsed = ref(false)
watch(() => !!overlayInvoiceId.value || !!composerInvoiceId.value, (open) => { if (open) invoiceOverlayUsed.value = true }, { immediate: true })
watch(filePreviewRequested, (asked) => { if (asked) filePreviewUsed.value = true }, { immediate: true })
if (typeof window !== 'undefined') {
    const idle = (cb: () => void) => {
        if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(cb, { timeout: 8000 })
        else setTimeout(cb, 4000)
    }
    idle(() => {
        void import('@/components/file-preview/FilePreviewHost.vue').then(() => { filePreviewUsed.value = true }, () => {})
        void import('@/components/invoices/InvoiceOverlayMount.vue').then(() => { invoiceOverlayUsed.value = true }, () => {})
    })
}

// One hour between background checks while the tab stays focused.
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000

// Reload exactly once when the user-requested update takes control. We drive the
// reload ourselves (rather than relying on the plugin's internal listener) so it
// fires deterministically once clientsClaim hands the page to the new worker.
// Gated on `userRequestedUpdate` so the initial clientsClaim on a first visit
// (which also fires controllerchange) does not trigger a spurious reload.
let userRequestedUpdate = false
let reloading = false

const needRefresh = ref(false)
let updateServiceWorker: () => void = () => {}
/** Desktop app: the shell cached a newer build itself, so Reload is a plain reload when no worker is waiting. */
let desktopUpdateReady = false
let serviceWorkerWaiting = () => false

/**
 * Native chrome (status bar style/color, keyboard resize behavior) has no
 * web equivalent to gate against — Web Push/SW update-detection below is
 * PWA-only and skipped on native.
 */
async function initNativeChrome() {
    document.documentElement.classList.add('native-app', `native-${getNativePlatform()}`)
    // WKWebView only applies :active styles when a touchstart listener exists.
    document.addEventListener('touchstart', () => {}, { passive: true })
    installNativeZoomLock()
    // index.html already applied the stored theme before first paint (no exemption for '/' on native).
    const dark = document.documentElement.classList.contains('dark')
    const page = dark ? THEME_PAGE_DARK : THEME_PAGE_LIGHT
    document.documentElement.style.backgroundColor = page
    document.body.style.backgroundColor = page
    // The status bar (drawn over the WebView; the shell pads with safe-area-inset-top) and the Android
    // navigation bar follow the theme in appearance.ts, synced only when the resolved theme changes.
    try {
        // Native resize everywhere except the iOS auth screen, which opts into
        // a synced inset animation (see nativeAuthKeyboard.ts).
        await applyNativeKeyboardMode()
    } catch {
        // Keyboard plugin unavailable on this platform — ignore.
    }
}

// Quick actions, push routing, lifecycle and the rest of the native wiring start in main.ts (src/native/nativeApp.ts).
if (isNativePlatform()) {
    void initNativeChrome()
}

// Desktop app (Electron): window chrome, menu commands, session hand-off, Quick Ask. Loaded on demand so
// the web app's entry chunk never carries it; the shell queues commands until the integration is ready.
if (desktopApp) {
    const desktopRouter = useRouter()
    void import('@/desktop/init').then(({ initDesktopShell }) => {
        initDesktopShell(desktopRouter, {
            toggleTheme: toggleLightDark,
            setThemePreference,
            onWebUpdateReady: () => {
                desktopUpdateReady = true
                needRefresh.value = true
            },
        })
    }).catch((err) => console.error('[desktop] integration failed to load', err))
}

if (!GUEST_MODE && !isNativePlatform()) {
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (!userRequestedUpdate || reloading) return
            reloading = true
            window.location.reload()
        })
    }

    const sw = useRegisterSW({
        immediate: true,
        onRegisteredSW(_swUrl, registration) {
            if (!registration) return
            // update() rejects (e.g. Safari's "newestWorker is null") when the
            // worker is mid-install or the page is backgrounded; a missed check
            // is harmless because the next visibility/interval tick retries.
            const checkForUpdate = () => {
                registration.update().catch(() => {})
            }
            // Re-check for a new deployment when the user returns to the tab.
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    checkForUpdate()
                }
            })
            // Also poll periodically so a long-focused tab still discovers updates.
            setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS)
        },
    })
    needRefresh.value = sw.needRefresh.value
    watch(sw.needRefresh, (v) => {
        needRefresh.value = v || desktopUpdateReady
    })
    updateServiceWorker = () => sw.updateServiceWorker()
    serviceWorkerWaiting = () => sw.needRefresh.value
}

// Registering Syncfusion license key
// registerLicense('Ngo9BigBOggjHTQxAR8/V1NCaF1cXGJCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdnWXdceHRcQmJdWEN1XEY=')
// registerLicense('NxYtFisQPR08Cit/VkV+XU9AclRDX3xKf0x/TGpQb19xflBPallYVBYiSV9jS3hTdUdjWHxdcXVWR2NVUE91XA==;Ix0oFS8QJAw9HSQvXkVjQlBacltJXGFWfVJpTGpQdk5xdV9DaVZUTWY/P1ZhSXxVdkZhWX5dc3VURWFZVUN9XEE=;NxYtGyMROh0gHDMgDk1jX09FaFtGVmJLYVB3WmpQdldgdVRMZVVbQX9PIiBoS35RcEVgW39fc3dUR2NdVkR/VEFe')

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const isProd = isProductionEnv()

if (!GUEST_MODE) {
    useActivityHeartbeat(() => authStore.isAuthenticated)

    function syncMetaPixelForCurrentRoute() {
        syncMetaPixel({
            route: route,
            isAuthenticated: authStore.isAuthenticated,
            authReady: authStore.authReady,
        })
    }

    watch(
        () => [authStore.authReady, authStore.isAuthenticated, route.name, route.path] as const,
        syncMetaPixelForCurrentRoute,
        { immediate: true },
    )

    // Referral links can also be shared as ?ref= on any public page, not just /r/:code.
    watch(
        () => route.query,
        (query) => {
            captureReferralFromQuery(query as Record<string, unknown>)
        },
        { immediate: true },
    )

    // if (import.meta.env.VITE_APP_ENV === 'production') {
    //     LogRocket.init('mdp7er/lighthouse')
    // }

    supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
            // Partner-only accounts (ambassadors without a Lighthouse profile)
            // belong in the partner portal, never the product session. Local
            // sign-out so a portal session on the same account survives.
            if (session?.user?.app_metadata?.account_type === 'partner') {
                await supabase.auth.signOut({ scope: 'local' }).catch(() => {})
                if (!router.currentRoute.value.path.startsWith('/partners')) {
                    router.replace({ name: 'partner-login' })
                }
                return
            }
            const wasAuthenticated = authStore.isAuthenticated
            authStore.setUser(session?.user ?? null)
            authStore.authReady = true
            authStore.loadRedirectRoute()
            syncMetaPixelForCurrentRoute()

            // Start the system announcement listener once the user is authenticated.
            if (session?.user) {
                startSystemAnnouncementListener()
                // Claim a referral cookie on the first authenticated load. The
                // backend ignores it for anyone who isn't a genuine new signup.
                claimStoredReferral().catch(() => {})
                // Stitch lh_anon_id only for a new account (or a pending signup).
                maybeStitchIdentity(session.user.created_at).catch(() => {})
            }

            if (!wasAuthenticated && !authStore.redirectRoute && authStore.isAuthenticated && !nativeGoogleAuthPending.value) {
                const currentRoute = router.currentRoute.value
                const currentPath = currentRoute.path
                const isOAuthVerifyCallback =
                    currentPath === '/verify' && currentRoute.query.continue === 'true'
                const publicPaths = ['/', '/verify', '/our-story', '/press', '/ambassador', '/inbox', '/pricing-guide']
                // A restored session should leave the standalone login, same as the marketing home.
                if ((isNativePlatform() || isDesktopShell()) && currentPath === '/native-login') {
                    publicPaths.push('/native-login')
                }
                if (publicPaths.includes(currentPath) && !isOAuthVerifyCallback) {
                    await router.isReady()
                    // App shells replace the login entry: otherwise Android back at the dashboard root walks
                    // back to the login (which bounces forward again) instead of minimizing the app.
                    if (isNativePlatform() || isDesktopShell()) router.replace(signedInHomeRoute(session?.user?.email))
                    else router.push(signedInHomeRoute(session?.user?.email))
                }
            }
        }
        else if (event === 'SIGNED_OUT') {
            // The native boot cache holds this account's workspace: it must not outlive the session.
            clearBootCache()
            authStore.clearUser()
            authStore.authReady = true
            syncMetaPixelForCurrentRoute()
            stopSystemAnnouncementListener()
        }
    })

    // If impersonation state is left over but the Supabase session is gone (e.g. the
    // target session expired and could not refresh), clear it and warn the staff user.
    reconcileImpersonation().then((cleared) => {
        if (cleared) {
            showToast({
                type: 'info',
                message: 'Impersonation session ended. You are back to your own account.',
            })
        }
    })

    // When a new deployment is detected, surface a non-blocking toast so the user
    // can reload on their own terms instead of being interrupted mid-task.
    const updateToastShown = ref(false)

    watch(needRefresh, (refresh) => {
        if (!refresh || updateToastShown.value) return

        updateToastShown.value = true
        showToast({
            type: 'info',
            message: 'A new version of Lighthouse is available.',
            duration: 0,
            action: {
                label: 'Reload',
                handler: () => {
                    userRequestedUpdate = true
                    if (desktopUpdateReady && !serviceWorkerWaiting()) {
                        window.location.reload()
                        return
                    }
                    // Posts SKIP_WAITING; the new worker activates, clientsClaim takes
                    // control, and our controllerchange handler reloads the page.
                    updateServiceWorker()
                },
            },
        })
    })

    authStore.$onAction(({ name, store, after }) => {
        if (name === 'loadRedirectRoute') {
            after(async () => {
                const redirectRoute = store.redirectRoute

                if (redirectRoute) {
                    await router.isReady()
                    await router.replace(redirectRoute as RouteLocationRaw)
                    authStore.clearRedirectRoute()
                }
            })
        }
    })
}
else {
    // Guest mode: mark auth ready with no session so any leftover watchers settle.
    authStore.authReady = true
}
</script>

<template>
  <div class="absolute inset-0 flex flex-col">
    <ImpersonationBanner />
    <SupportAccessBanner />
    <AppLayout>
      <RouterView />
    </AppLayout>
  </div>
  <InvoiceOverlayMount v-if="invoiceOverlayUsed" />
  <FilePreviewHost v-if="filePreviewUsed" />
  <ToastContainer />
  <ActionSheetHost />
  <VoiceCallHost v-if="!GUEST_MODE" />
  <InAppBanner />
  <OfflineBanner />
  <KeyboardShortcutsModal v-if="desktopApp" />
  <!-- Version gate, AI consent, support-access requests, account-deleted confirmation (store readiness). -->
  <AppGates />
</template>

<style scoped>
header {
    line-height: 1.5;
    max-height: 100vh;
}

.logo {
    display: block;
    margin: 0 auto 2rem;
}

nav {
    width: 100%;
    font-size: 12px;
    text-align: center;
    margin-top: 2rem;
}

nav a.router-link-exact-active {
    color: var(--color-text);
}

nav a.router-link-exact-active:hover {
    background-color: transparent;
}

nav a {
    display: inline-block;
    padding: 0 1rem;
    border-left: 1px solid var(--color-border);
}

nav a:first-of-type {
    border: 0;
}

@media (min-width: 1024px) {
    header {
        display: flex;
        place-items: center;
        padding-right: calc(var(--section-gap) / 2);
    }

    .logo {
        margin: 0 2rem 0 0;
    }

    header .wrapper {
        display: flex;
        place-items: flex-start;
        flex-wrap: wrap;
    }

    nav {
        text-align: left;
        margin-left: -1rem;
        font-size: 1rem;

        padding: 1rem 0;
        margin-top: 1rem;
    }
}
</style>
